import { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Activity, RefreshCw, Loader2, Database, Server,
  Zap, AlertTriangle, CheckCircle, XCircle, Clock, TrendingUp,
  ShoppingBag, Gauge, Layers, Bell, BellOff, Shield, ShieldAlert,
  ChevronDown, ChevronUp, Eye
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell
} from "recharts";

function fmt(ms) {
  if (!ms && ms !== 0) return "-";
  if (ms < 1) return "<1ms";
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function StatusDot({ ok }) {
  return (
    <span
      className={`inline-block w-2 h-2 rounded-full ${ok ? "bg-emerald-500" : "bg-red-500"}`}
      data-testid={ok ? "status-ok" : "status-error"}
    />
  );
}

function MetricCard({ label, value, sub, icon: Icon, color = "text-emerald-500" }) {
  return (
    <Card data-testid={`metric-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <CardContent className="p-4 flex items-center gap-3">
        <div className={`p-2 rounded-lg bg-muted ${color}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs text-muted-foreground font-medium">{label}</p>
          <p className="text-lg font-bold font-mono">{value}</p>
          {sub && <p className="text-[10px] text-muted-foreground">{sub}</p>}
        </div>
      </CardContent>
    </Card>
  );
}

const EP_COLORS = ["#10b981", "#3b82f6", "#f59e0b", "#8b5cf6", "#ec4899", "#06b6d4", "#f97316", "#84cc16", "#6366f1", "#ef4444"];

const SEVERITY_CONFIG = {
  healthy:  { color: "text-emerald-500", bg: "bg-emerald-500/10", border: "border-emerald-500/30", icon: Shield, label: "Saudavel" },
  warning:  { color: "text-amber-500",   bg: "bg-amber-500/10",   border: "border-amber-500/30",   icon: AlertTriangle, label: "Alerta" },
  critical: { color: "text-red-500",     bg: "bg-red-500/10",     border: "border-red-500/30",     icon: ShieldAlert, label: "Critico" },
};

function AlertBanner({ alerts }) {
  const [expanded, setExpanded] = useState(false);
  if (!alerts) return null;

  const status = alerts.status || "healthy";
  const active = alerts.active_alerts || [];
  const cfg = SEVERITY_CONFIG[status] || SEVERITY_CONFIG.healthy;
  const Icon = cfg.icon;

  return (
    <div className={`rounded-lg border ${cfg.border} ${cfg.bg} overflow-hidden`} data-testid="alert-banner">
      <button
        className="w-full flex items-center gap-3 p-3 text-left"
        onClick={() => setExpanded(!expanded)}
        data-testid="alert-banner-toggle"
      >
        <Icon className={`w-5 h-5 ${cfg.color} shrink-0`} />
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className={`text-sm font-bold ${cfg.color}`}>{cfg.label}</span>
            {active.length > 0 && (
              <Badge variant="outline" className={`text-[10px] h-5 ${cfg.color} ${cfg.border}`}>
                {active.length} alerta{active.length !== 1 ? "s" : ""} ativo{active.length !== 1 ? "s" : ""}
              </Badge>
            )}
          </div>
          {active.length === 0 && (
            <p className="text-xs text-muted-foreground mt-0.5">Todos os sistemas operando normalmente</p>
          )}
        </div>
        {active.length > 0 && (
          expanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />
        )}
      </button>

      {expanded && active.length > 0 && (
        <div className="border-t border-muted/30 p-3 space-y-2" data-testid="active-alerts-list">
          {active.map((a, i) => {
            const aCfg = SEVERITY_CONFIG[a.severity] || SEVERITY_CONFIG.warning;
            return (
              <div key={a.id || i} className={`flex items-start gap-2 p-2 rounded ${aCfg.bg} text-xs`} data-testid={`active-alert-${a.key}`}>
                <aCfg.icon className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${aCfg.color}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium">{a.title}</span>
                    <Badge variant="outline" className={`text-[9px] h-4 ${aCfg.color} ${aCfg.border}`}>{a.severity}</Badge>
                    {a.acknowledged && <Badge variant="outline" className="text-[9px] h-4 text-blue-400 border-blue-400/30">ACK</Badge>}
                  </div>
                  <div className="flex gap-3 mt-1 text-muted-foreground flex-wrap">
                    {a.details?.endpoint && <span className="font-mono">{a.details.endpoint}</span>}
                    {a.details?.p95_ms !== undefined && <span>p95: {fmt(a.details.p95_ms)}</span>}
                    {a.details?.error_rate_pct !== undefined && <span>err: {a.details.error_rate_pct}%</span>}
                    {a.details?.service && <span>{a.details.service}: {a.details.status || a.details.depth}</span>}
                    <span>{a.triggered_at?.slice(11, 19)}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function AlertHistory({ history, show, onToggle }) {
  if (!history || history.length === 0) return null;
  return (
    <Card data-testid="alert-history">
      <CardContent className="p-4">
        <button className="flex items-center gap-2 w-full text-left" onClick={onToggle}>
          <Bell className="w-4 h-4 text-amber-500" />
          <p className="text-xs font-bold text-muted-foreground uppercase tracking-wide flex-1">Historico de Alertas ({history.length})</p>
          {show ? <ChevronUp className="w-3 h-3 text-muted-foreground" /> : <ChevronDown className="w-3 h-3 text-muted-foreground" />}
        </button>
        {show && (
          <ScrollArea className="h-[250px] mt-3">
            <div className="space-y-1.5">
              {history.map((a, i) => {
                const aCfg = SEVERITY_CONFIG[a.severity] || SEVERITY_CONFIG.warning;
                const resolved = !!a.resolved_at;
                return (
                  <div key={a.id || i} className={`flex items-center gap-2 text-xs p-1.5 rounded ${resolved ? "opacity-60" : ""}`}>
                    <aCfg.icon className={`w-3 h-3 shrink-0 ${aCfg.color}`} />
                    <Badge variant="outline" className={`text-[9px] h-4 ${aCfg.color} ${aCfg.border} shrink-0`}>{a.severity}</Badge>
                    <span className="truncate flex-1">{a.title}</span>
                    {resolved && <CheckCircle className="w-3 h-3 text-emerald-500 shrink-0" />}
                    <span className="text-muted-foreground shrink-0 font-mono">{(a.triggered_at || "").slice(11, 19)}</span>
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
}

export function ObservabilityTab({ adminAxios }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [showHistory, setShowHistory] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const res = await adminAxios.get("/platform-admin/observability");
      setData(res.data);
    } catch {
      /* ignore */
    }
    setLoading(false);
  }, [adminAxios]);

  useEffect(() => {
    fetchData();
    if (!autoRefresh) return;
    const i = setInterval(fetchData, 15000);
    return () => clearInterval(i);
  }, [fetchData, autoRefresh]);

  if (loading && !data) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!data) {
    return (
      <Card>
        <CardContent className="p-8 text-center text-muted-foreground">
          <AlertTriangle className="w-8 h-8 mx-auto mb-2 opacity-40" />
          <p>Erro ao carregar dados de observabilidade</p>
          <Button size="sm" className="mt-3" onClick={fetchData}>Tentar novamente</Button>
        </CardContent>
      </Card>
    );
  }

  const rm = data.request_metrics || {};
  const tq = data.task_queue || {};
  const stats = tq.stats || {};
  const cache = data.cache || {};
  const orders = data.orders || {};
  const dbHealth = data.database || {};
  const alerts = data.alerts || {};

  const statusData = Object.entries(rm.status_distribution || {}).map(([code, count]) => ({
    name: code, value: count,
    fill: code.startsWith("2") ? "#10b981" : code.startsWith("4") ? "#f59e0b" : "#ef4444",
  }));

  const endpointData = (rm.by_endpoint || []).slice(0, 10).map(ep => ({
    name: ep.path,
    p50: ep.p50,
    p95: ep.p95,
    count: ep.count,
  }));

  const volumeData = (orders.volume_24h || []).slice(0, 10).map(o => ({
    name: (o.restaurant_name || "").substring(0, 15),
    pedidos: o.count,
    receita: o.revenue,
  }));

  return (
    <div className="space-y-5" data-testid="observability-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Activity className="w-5 h-5 text-emerald-500" />
          <h2 className="font-bold text-lg">Observabilidade</h2>
          <Badge variant="outline" className="text-[10px]">
            Janela: {rm.window_seconds || 600}s
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant={autoRefresh ? "default" : "outline"}
            className="text-xs gap-1 h-7"
            onClick={() => setAutoRefresh(!autoRefresh)}
            data-testid="auto-refresh-toggle"
          >
            {autoRefresh ? <Zap className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
            {autoRefresh ? "Auto 15s" : "Pausado"}
          </Button>
          <Button size="sm" variant="outline" className="text-xs gap-1 h-7" onClick={() => { setLoading(true); fetchData(); }} data-testid="refresh-observability">
            <RefreshCw className="w-3 h-3" /> Atualizar
          </Button>
        </div>
      </div>

      {/* Alert Banner */}
      <AlertBanner alerts={alerts} />

      {/* Top Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MetricCard label="RPS" value={rm.rps || 0} sub={`${rm.requests_in_window || 0} reqs na janela`} icon={Gauge} color="text-blue-500" />
        <MetricCard label="Latencia p50" value={fmt(rm.latency?.p50)} sub={`p95: ${fmt(rm.latency?.p95)}`} icon={Clock} color="text-amber-500" />
        <MetricCard label="Taxa de Erro" value={`${rm.error_rate_pct || 0}%`} sub={`5xx na janela`} icon={AlertTriangle} color={rm.error_rate_pct > 5 ? "text-red-500" : "text-emerald-500"} />
        <MetricCard label="Pedidos Hoje" value={orders.today || 0} sub={`${(orders.volume_24h || []).length} restaurantes ativos`} icon={ShoppingBag} color="text-purple-500" />
      </div>

      {/* System Health Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card data-testid="health-db">
          <CardContent className="p-3 flex items-center gap-2">
            <Database className="w-4 h-4 text-blue-500" />
            <div className="flex-1">
              <p className="text-xs font-medium">Database</p>
              <div className="flex items-center gap-1">
                <StatusDot ok={dbHealth.status === "healthy"} />
                <span className="text-xs text-muted-foreground">{dbHealth.status}</span>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="health-cache">
          <CardContent className="p-3 flex items-center gap-2">
            <Layers className="w-4 h-4 text-amber-500" />
            <div className="flex-1">
              <p className="text-xs font-medium">Cache</p>
              <div className="flex items-center gap-1">
                <StatusDot ok={cache.status === "connected" || cache.status === "active"} />
                <span className="text-xs text-muted-foreground">
                  {cache.backend || "?"} ({cache.status})
                  {cache.entries !== undefined && ` - ${cache.entries} keys`}
                  {cache.keys !== undefined && ` - ${cache.keys} keys`}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="health-queue">
          <CardContent className="p-3 flex items-center gap-2">
            <Server className="w-4 h-4 text-purple-500" />
            <div className="flex-1">
              <p className="text-xs font-medium">Task Queue</p>
              <div className="flex items-center gap-1">
                <StatusDot ok={tq.connection?.status === "connected"} />
                <span className="text-xs text-muted-foreground">
                  {tq.connection?.status || "desconhecido"}
                  {tq.connection?.queued_jobs !== undefined && ` - ${tq.connection.queued_jobs} na fila`}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="health-tasks-summary">
          <CardContent className="p-3 flex items-center gap-2">
            <Zap className="w-4 h-4 text-emerald-500" />
            <div className="flex-1">
              <p className="text-xs font-medium">Tasks ({stats.total || 0})</p>
              <div className="flex gap-2 text-[10px]">
                <span className="text-emerald-600">{stats.success || 0} ok</span>
                <span className="text-red-500">{stats.failed || 0} falhas</span>
                <span className="text-yellow-600">{stats.running || 0} exec</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* Endpoint Latency */}
        {endpointData.length > 0 && (
          <Card data-testid="chart-endpoint-latency">
            <CardContent className="p-4">
              <p className="text-xs font-bold mb-3 text-muted-foreground uppercase tracking-wide">Latencia por Endpoint (ms)</p>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={endpointData} layout="vertical" margin={{ left: 80, right: 10, top: 5, bottom: 5 }}>
                  <XAxis type="number" tick={{ fontSize: 10 }} />
                  <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} width={75} />
                  <Tooltip
                    contentStyle={{ fontSize: 11, borderRadius: 8 }}
                    formatter={(v) => [`${v}ms`]}
                  />
                  <Bar dataKey="p50" fill="#10b981" name="p50" radius={[0, 4, 4, 0]} barSize={8} />
                  <Bar dataKey="p95" fill="#f59e0b" name="p95" radius={[0, 4, 4, 0]} barSize={8} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Order Volume */}
        {volumeData.length > 0 && (
          <Card data-testid="chart-order-volume">
            <CardContent className="p-4">
              <p className="text-xs font-bold mb-3 text-muted-foreground uppercase tracking-wide">Volume de Pedidos (24h)</p>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={volumeData} margin={{ left: 5, right: 10, top: 5, bottom: 5 }}>
                  <XAxis dataKey="name" tick={{ fontSize: 9 }} angle={-15} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip
                    contentStyle={{ fontSize: 11, borderRadius: 8 }}
                    formatter={(v, name) => [name === "receita" ? `${v} EUR` : v, name]}
                  />
                  <Bar dataKey="pedidos" fill="#8b5cf6" name="Pedidos" radius={[4, 4, 0, 0]} barSize={20}>
                    {volumeData.map((_, i) => <Cell key={i} fill={EP_COLORS[i % EP_COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Status Distribution + Endpoint Table */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* Status Distribution */}
        <Card data-testid="status-distribution">
          <CardContent className="p-4">
            <p className="text-xs font-bold mb-3 text-muted-foreground uppercase tracking-wide">Distribuicao de Status</p>
            <div className="space-y-2">
              {statusData.map(s => {
                const total = statusData.reduce((a, b) => a + b.value, 0);
                const pct = total > 0 ? ((s.value / total) * 100).toFixed(1) : 0;
                return (
                  <div key={s.name} className="flex items-center gap-2">
                    <span className="text-xs font-mono w-8">{s.name}</span>
                    <div className="flex-1 bg-muted rounded-full h-3 overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${pct}%`, background: s.fill }} />
                    </div>
                    <span className="text-xs font-mono w-16 text-right">{s.value} ({pct}%)</span>
                  </div>
                );
              })}
              {statusData.length === 0 && <p className="text-xs text-muted-foreground text-center py-4">Sem dados ainda</p>}
            </div>
          </CardContent>
        </Card>

        {/* Endpoint Table */}
        <Card data-testid="endpoint-table">
          <CardContent className="p-4">
            <p className="text-xs font-bold mb-3 text-muted-foreground uppercase tracking-wide">Top Endpoints</p>
            <ScrollArea className="h-[200px]">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-muted-foreground border-b">
                    <th className="text-left pb-2">Path</th>
                    <th className="text-right pb-2">Reqs</th>
                    <th className="text-right pb-2">p50</th>
                    <th className="text-right pb-2">p95</th>
                    <th className="text-right pb-2">Erros</th>
                  </tr>
                </thead>
                <tbody>
                  {(rm.by_endpoint || []).map((ep, i) => (
                    <tr key={i} className="border-b border-muted/50">
                      <td className="py-1.5 font-mono truncate max-w-[140px]">{ep.path}</td>
                      <td className="text-right font-mono">{ep.count}</td>
                      <td className="text-right font-mono">{fmt(ep.p50)}</td>
                      <td className="text-right font-mono">{fmt(ep.p95)}</td>
                      <td className="text-right">
                        {ep.errors > 0 ? (
                          <span className="text-red-500 font-mono">{ep.errors}</span>
                        ) : (
                          <span className="text-emerald-500">0</span>
                        )}
                      </td>
                    </tr>
                  ))}
                  {(rm.by_endpoint || []).length === 0 && (
                    <tr><td colSpan={5} className="text-center py-4 text-muted-foreground">Sem dados</td></tr>
                  )}
                </tbody>
              </table>
            </ScrollArea>
          </CardContent>
        </Card>
      </div>

      {/* Task Stats by Name + Recent Errors */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* Task Stats */}
        <Card data-testid="task-stats-by-name">
          <CardContent className="p-4">
            <p className="text-xs font-bold mb-3 text-muted-foreground uppercase tracking-wide">Tasks por Tipo</p>
            <ScrollArea className="h-[200px]">
              <div className="space-y-1.5">
                {(tq.by_task || []).map((t, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs">
                    <span className="font-mono truncate flex-1">{(t.task_name || "").replace("task_", "")}</span>
                    <Badge variant="outline" className="text-[10px] h-5">{t.total}</Badge>
                    <span className="text-emerald-600 w-8 text-right">{t.success}</span>
                    <span className="text-red-500 w-6 text-right">{t.failed}</span>
                    <span className="text-muted-foreground w-14 text-right font-mono">{fmt(t.avg_duration_ms)}</span>
                  </div>
                ))}
                {(tq.by_task || []).length === 0 && (
                  <p className="text-center text-muted-foreground py-4">Nenhuma task registrada</p>
                )}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Recent Errors */}
        <Card data-testid="recent-errors">
          <CardContent className="p-4">
            <p className="text-xs font-bold mb-3 text-muted-foreground uppercase tracking-wide">Erros Recentes (5xx)</p>
            <ScrollArea className="h-[200px]">
              <div className="space-y-1.5">
                {(rm.recent_errors || []).map((e, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs border-b border-muted/30 pb-1">
                    <XCircle className="w-3 h-3 text-red-500 shrink-0" />
                    <span className="font-mono text-red-500 w-8">{e.status}</span>
                    <span className="font-mono truncate flex-1">{e.path}</span>
                    <span className="text-muted-foreground font-mono">{fmt(e.duration_ms)}</span>
                    <span className="text-[10px] text-muted-foreground">{e.timestamp?.slice(11, 19)}</span>
                  </div>
                ))}
                {(rm.recent_errors || []).length === 0 && (
                  <div className="text-center py-6 text-muted-foreground">
                    <CheckCircle className="w-6 h-6 mx-auto mb-1 text-emerald-500 opacity-50" />
                    <p>Nenhum erro 5xx recente</p>
                  </div>
                )}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      </div>

      {/* Alert History */}
      <AlertHistory
        history={alerts.history || []}
        show={showHistory}
        onToggle={() => setShowHistory(!showHistory)}
      />

      {/* Recent Failed Tasks */}
      {(tq.recent_failures || []).length > 0 && (
        <Card data-testid="recent-failed-tasks">
          <CardContent className="p-4">
            <p className="text-xs font-bold mb-3 text-muted-foreground uppercase tracking-wide">Tasks Falhadas Recentes</p>
            <ScrollArea className="h-[200px]">
              <div className="space-y-2">
                {tq.recent_failures.map((t, i) => (
                  <div key={i} className="text-xs border-b border-muted/30 pb-2">
                    <div className="flex items-center gap-2">
                      <XCircle className="w-3 h-3 text-red-500 shrink-0" />
                      <span className="font-mono font-medium">{(t.task_name || "").replace("task_", "")}</span>
                      <span className="text-muted-foreground">{fmt(t.duration_ms)}</span>
                      <span className="text-[10px] text-muted-foreground ml-auto">{t.finished_at?.slice(0, 19)?.replace("T", " ")}</span>
                    </div>
                    {t.error && <p className="text-red-400 text-[10px] mt-0.5 truncate pl-5">{t.error}</p>}
                  </div>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
